/**
 * ContactMap
 *
 * Mapa MapLibre GL JS com:
 *  - Marcadores clusterizados de contatos (quando lat/lng disponível)
 *  - Layer de heatmap por densidade
 *  - Popup ao clicar em marcador
 *  - Foco automático em cluster ao selecionar bairro
 *
 * Tiles: OpenFreeMap (sem API key, baseado em OSM)
 */

import { useEffect, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { ContactMapPoint } from '../hooks/useGeoData';
import type { VoiceNoteGeoPin } from '@/features/crm/hooks/useVoiceNotes';

// ── Cores por categoria ───────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  lideranca:   '#00C2FF',
  equipe:      '#8B5CF6',
  apoiador:    '#10B981',
  militante:   '#F59E0B',
  eleitor:     '#6B7280',
  default:     '#94A3B8',
};

// ── Cores por sentimento (voice notes) ────────────────────────────────────────

const SENTIMENT_COLORS: Record<string, string> = {
  positive: '#10B981',
  neutral:  '#94A3B8',
  negative: '#EF4444',
  mixed:    '#FBBF24',
};

// ── Estilos do mapa (dark theme compatível com a UI) ─────────────────────────

const MAP_STYLE = 'https://tiles.openfreemap.org/styles/dark';

// Fallback leve caso OpenFreeMap esteja indisponível
const MAP_STYLE_FALLBACK: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm-tiles', type: 'raster', source: 'osm' }],
};

// ── Props ──────────────────────────────────────────────────────────────────────

interface ContactMapProps {
  points:           ContactMapPoint[];
  voicePins?:       VoiceNoteGeoPin[];   // Sobrepor notas de voz georreferenciadas
  showVoicePins?:   boolean;             // Toggle externo
  focusNeighborhood?: string | null;
  onPointClick?:    (point: ContactMapPoint) => void;
  className?:       string;
  /** Centro inicial do mapa [longitude, latitude]. Padrão: Brasil */
  defaultCenter?:   [number, number];
  /** Zoom inicial. Padrão: 4 (Brasil inteiro) */
  defaultZoom?:     number;
}

// ── Componente ─────────────────────────────────────────────────────────────────

export function ContactMap({
  points,
  voicePins,
  showVoicePins = false,
  focusNeighborhood,
  onPointClick,
  className = '',
  defaultCenter = [-47.9, -15.8],
  defaultZoom   = 4,
}: ContactMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<maplibregl.Map | null>(null);
  const popupRef     = useRef<maplibregl.Popup | null>(null);
  const voicePopupRef = useRef<maplibregl.Popup | null>(null);

  // Converte pontos para GeoJSON
  const toGeoJSON = useCallback((pts: ContactMapPoint[]): GeoJSON.FeatureCollection => ({
    type: 'FeatureCollection',
    features: pts.map((p) => ({
      type:     'Feature',
      geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
      properties: {
        id:               p.id,
        full_name:        p.full_name,
        category:         p.category,
        engagement_score: p.engagement_score,
        neighborhood:     p.neighborhood ?? '',
        city:             p.city ?? '',
        color:            CATEGORY_COLORS[p.category] ?? CATEGORY_COLORS.default,
      },
    })),
  }), []);

  // Converte pins de notas de voz em GeoJSON
  const toVoiceGeoJSON = useCallback((pins: VoiceNoteGeoPin[]): GeoJSON.FeatureCollection => ({
    type: 'FeatureCollection',
    features: pins.map((p) => ({
      type:     'Feature',
      geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
      properties: {
        id:                   p.id,
        contact_id:           p.contact_id,
        sentiment:            p.sentiment ?? 'neutral',
        themes:               (p.themes ?? []).join(', '),
        summary:              p.summary ?? '',
        engagement_indicator: p.engagement_indicator ?? 0,
        created_at:           p.created_at,
        color:                SENTIMENT_COLORS[p.sentiment ?? 'neutral'] ?? SENTIMENT_COLORS.neutral,
      },
    })),
  }), []);

  // Inicializa o mapa (roda uma única vez; defaultCenter/defaultZoom são lidos na criação)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style:     MAP_STYLE,
      center:    defaultCenter,
      zoom:      defaultZoom,
      attributionControl: false,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');

    map.on('error', () => {
      // Fallback para mapa sem tiles externos
      map.setStyle(MAP_STYLE_FALLBACK);
    });

    map.on('load', () => {
      // Source clusterizado
      map.addSource('contacts', {
        type:    'geojson',
        data:    toGeoJSON([]),
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius:  50,
      });

      // Layer: círculos de cluster
      map.addLayer({
        id:     'clusters',
        type:   'circle',
        source: 'contacts',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step', ['get', 'point_count'],
            '#0EA5E9',   10,
            '#8B5CF6',   50,
            '#F59E0B',
          ],
          'circle-radius':  ['step', ['get', 'point_count'], 16, 10, 24, 50, 32],
          'circle-opacity': 0.85,
          'circle-stroke-width': 2,
          'circle-stroke-color': 'rgba(255,255,255,0.2)',
        },
      });

      // Layer: contagem do cluster
      map.addLayer({
        id:     'cluster-count',
        type:   'symbol',
        source: 'contacts',
        filter: ['has', 'point_count'],
        layout: {
          'text-field':      '{point_count_abbreviated}',
          'text-font':       ['Open Sans Bold'],
          'text-size':       12,
        },
        paint: { 'text-color': '#fff' },
      });

      // Layer: pontos individuais
      map.addLayer({
        id:     'unclustered-point',
        type:   'circle',
        source: 'contacts',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color':        ['get', 'color'],
          'circle-radius':       6,
          'circle-opacity':      0.9,
          'circle-stroke-width': 1.5,
          'circle-stroke-color': 'rgba(255,255,255,0.4)',
        },
      });

      // Heatmap layer (zoom < 8)
      map.addLayer({
        id:     'heatmap',
        type:   'heatmap',
        source: 'contacts',
        maxzoom: 9,
        paint: {
          'heatmap-weight':    ['interpolate', ['linear'], ['get', 'engagement_score'], 0, 0, 100, 1],
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 9, 3],
          'heatmap-color': [
            'interpolate', ['linear'], ['heatmap-density'],
            0,   'rgba(0,194,255,0)',
            0.2, 'rgba(0,194,255,0.3)',
            0.5, 'rgba(139,92,246,0.6)',
            0.8, 'rgba(245,158,11,0.8)',
            1,   'rgba(239,68,68,0.9)',
          ],
          'heatmap-radius':   ['interpolate', ['linear'], ['zoom'], 0, 2, 9, 20],
          'heatmap-opacity':  ['interpolate', ['linear'], ['zoom'], 7, 0.8, 9, 0.2],
        },
      }, 'clusters');

      // ── Voice notes source + layer ──────────────────────────────────────────
      map.addSource('voice-notes', {
        type: 'geojson',
        data: toVoiceGeoJSON([]),
      });

      // Layer principal: pin colorido por sentimento (ícone de microfone via círculo + borda)
      map.addLayer({
        id:     'voice-pins',
        type:   'circle',
        source: 'voice-notes',
        layout: { visibility: 'none' }, // controlado pela prop showVoicePins
        paint: {
          'circle-color':        ['get', 'color'],
          'circle-radius':       8,
          'circle-opacity':      0.92,
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Halo pulsante (decorativo)
      map.addLayer({
        id:     'voice-pins-halo',
        type:   'circle',
        source: 'voice-notes',
        layout: { visibility: 'none' },
        paint: {
          'circle-color':   ['get', 'color'],
          'circle-radius':  14,
          'circle-opacity': 0.18,
        },
      }, 'voice-pins');

      // Clique em pin de voz → popup com resumo
      map.on('click', 'voice-pins', (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const props  = feature.properties as Record<string, unknown>;
        const coords = (feature.geometry as GeoJSON.Point).coordinates as [number, number];

        const sentLabel =
          props.sentiment === 'positive' ? 'Positivo' :
          props.sentiment === 'negative' ? 'Negativo' :
          props.sentiment === 'mixed'    ? 'Misto'    : 'Neutro';

        voicePopupRef.current?.remove();
        voicePopupRef.current = new maplibregl.Popup({ closeButton: true, maxWidth: '260px' })
          .setLngLat(coords)
          .setHTML(`
            <div style="font-family:sans-serif;font-size:12px;padding:6px 4px;line-height:1.5;">
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${props.color}"></span>
                <strong style="color:#0A1628;">Nota de voz · ${sentLabel}</strong>
              </div>
              ${props.summary ? `<div style="color:#1f2937;margin-bottom:6px;">${String(props.summary).replace(/</g, '&lt;')}</div>` : ''}
              ${props.themes ? `<div style="color:#64748B;font-size:10px;"><b>Temas:</b> ${props.themes}</div>` : ''}
              ${Number(props.engagement_indicator) > 0 ? `<div style="color:#64748B;font-size:10px;"><b>Engajamento:</b> ${props.engagement_indicator}/100</div>` : ''}
              <a href="/app/contatos/${props.contact_id}" style="display:inline-block;margin-top:6px;color:#00C2FF;font-size:11px;font-weight:600;">Ver contato →</a>
            </div>
          `)
          .addTo(map);
      });

      map.on('mouseenter', 'voice-pins', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'voice-pins', () => { map.getCanvas().style.cursor = ''; });

      // Clique em cluster → zoom in
      map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        if (!features.length) return;
        const clusterId = features[0].properties?.cluster_id;
        (map.getSource('contacts') as maplibregl.GeoJSONSource)
          .getClusterExpansionZoom(clusterId)
          .then((zoom) => {
            if (zoom == null) return;
            const coords = (features[0].geometry as GeoJSON.Point).coordinates as [number, number];
            map.easeTo({ center: coords, zoom });
          })
          .catch(() => { /* silencia erros de cluster */ });
      });

      // Clique em ponto individual → popup
      map.on('click', 'unclustered-point', (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const props = feature.properties as Record<string, unknown>;
        const coords = (feature.geometry as GeoJSON.Point).coordinates as [number, number];

        popupRef.current?.remove();
        popupRef.current = new maplibregl.Popup({ closeButton: false, maxWidth: '220px' })
          .setLngLat(coords)
          .setHTML(`
            <div style="font-family:sans-serif;font-size:12px;padding:4px 2px;">
              <strong style="color:#0A1628">${props.full_name}</strong><br/>
              <span style="color:#64748B">${props.neighborhood || props.city || ''}</span><br/>
              <span style="background:${props.color};color:#fff;padding:1px 6px;border-radius:10px;font-size:10px">${props.category}</span>
              <span style="margin-left:6px;color:#64748B">⚡ ${props.engagement_score}</span>
            </div>
          `)
          .addTo(map);

        if (onPointClick) {
          const matching = {
            id:               String(props.id ?? ''),
            full_name:        String(props.full_name ?? ''),
            category:         String(props.category ?? ''),
            engagement_score: Number(props.engagement_score ?? 0),
            neighborhood:     props.neighborhood ? String(props.neighborhood) : null,
            city:             props.city ? String(props.city) : null,
            latitude:         coords[1],
            longitude:        coords[0],
          };
          onPointClick(matching);
        }
      });

      // Cursores
      map.on('mouseenter', 'clusters',           () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'clusters',           () => { map.getCanvas().style.cursor = ''; });
      map.on('mouseenter', 'unclustered-point',  () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'unclustered-point',  () => { map.getCanvas().style.cursor = ''; });
    });

    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, [toGeoJSON, toVoiceGeoJSON, onPointClick]);

  // Atualiza os pontos quando os dados mudam
  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    const source = map.getSource('contacts') as maplibregl.GeoJSONSource | undefined;
    if (source) source.setData(toGeoJSON(points));
  }, [points, toGeoJSON]);

  // Atualiza pins de voice notes
  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    const source = map.getSource('voice-notes') as maplibregl.GeoJSONSource | undefined;
    if (source) source.setData(toVoiceGeoJSON(voicePins ?? []));
  }, [voicePins, toVoiceGeoJSON]);

  // Toggle de visibilidade da layer de voice notes
  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    const visibility = showVoicePins ? 'visible' : 'none';
    if (map.getLayer('voice-pins'))      map.setLayoutProperty('voice-pins',      'visibility', visibility);
    if (map.getLayer('voice-pins-halo')) map.setLayoutProperty('voice-pins-halo', 'visibility', visibility);
    if (!showVoicePins) voicePopupRef.current?.remove();
  }, [showVoicePins]);

  // Foco em bairro selecionado
  useEffect(() => {
    if (!focusNeighborhood || !mapRef.current) return;
    const pts = points.filter((p) => p.neighborhood?.toLowerCase().includes(focusNeighborhood.toLowerCase()));
    if (!pts.length) return;

    const lats = pts.map((p) => p.latitude);
    const lngs = pts.map((p) => p.longitude);
    const bounds = new maplibregl.LngLatBounds(
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    );
    mapRef.current.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 600 });
  }, [focusNeighborhood, points]);

  return (
    <div
      ref={containerRef}
      className={`w-full h-full rounded-2xl overflow-hidden ${className}`}
      style={{ minHeight: '400px' }}
    />
  );
}
